import { DocumentNode, useQuery } from '@apollo/client';
import { captureMessage } from '@sentry/react';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useCallbackRef } from '@synoptic/ui-kit/utils/use-callback-ref.js';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { deleteSearchParams } from '@/utils/handle-search-param';

const pollInterval = 2000;
const maxPollAttempts = 20;

type MonitorAccessPollProps<TData, TVariables> = {
  query: DocumentNode;
  variables: TVariables;
  isVerified: (data: TData) => boolean;
  onVerify: () => void;
  onPollLimitReached: () => void;
};

const MonitorAccessPoll = <TData, TVariables>({
  query,
  variables,
  isVerified,
  onVerify,
  onPollLimitReached,
}: MonitorAccessPollProps<TData, TVariables>) => {
  const pollAttempts = useRef(0);

  useQuery<TData>(query, {
    variables: variables as Record<string, unknown>,
    pollInterval,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (isVerified(data)) {
        onVerify();
        return;
      }

      pollAttempts.current++;

      if (pollAttempts.current >= maxPollAttempts) {
        onPollLimitReached();
      }
    },
  });

  return null;
};

type Props<TData, TVariables> = {
  query: DocumentNode;
  variables: TVariables;
  isVerified: (data: TData) => boolean;
  onVerify: (paymentOption: 'card' | 'crypto') => void;
  accessAllowed?: boolean;
  cryptoPaymentInitiated?: boolean;
  cleanupParams?: string[];
};

export const MonitorAccess = <TData, TVariables>({
  query,
  variables,
  isVerified,
  onVerify,
  accessAllowed,
  cryptoPaymentInitiated,
  cleanupParams,
}: Props<TData, TVariables>) => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const cardPaymentSuccess = searchParams.get('success') === 'true';

  const paymentOption = cardPaymentSuccess ? 'card' : 'crypto';

  const shouldPoll = cryptoPaymentInitiated || cardPaymentSuccess;

  const [inPolling, setInPolling] = useState(false);

  const startDataPolling = useCallbackRef(() => {
    setInPolling(true);
  });

  const stopDataPolling = useCallbackRef(() => {
    setInPolling(false);

    setSearchParams(
      deleteSearchParams(searchParams, ['success', ...(cleanupParams ?? [])]),
      { replace: true },
    );
  });

  const onPaymentVerification = useCallbackRef(() => {
    onVerify(paymentOption);

    stopDataPolling();
  });

  const onPollLimitReached = useCallbackRef(() => {
    if (paymentOption === 'crypto') {
      /*
       * For card payments polling starts after success. It's needed just to be sure we got up-to-date data from backend
       * For crypto payments we don't know if the payment is finished so we infinitely poll until it's completed or canceled
       */
      return;
    }

    stopDataPolling();

    toast.error({
      title: `We couldn't verify your payment`,
    });
    captureMessage('Failed to verify payment', {
      level: 'error',
      extra: {
        paymentOption,
      },
    });
  });

  useEffect(() => {
    if (cardPaymentSuccess) {
      if (accessAllowed) {
        onPaymentVerification();
      } else {
        startDataPolling();
      }
    }
  }, [
    onPaymentVerification,
    cardPaymentSuccess,
    startDataPolling,
    accessAllowed,
  ]);

  useEffect(() => {
    if (cryptoPaymentInitiated) {
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          startDataPolling();
        } else {
          stopDataPolling();
        }
      };

      onVisibilityChange();

      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }
  }, [cryptoPaymentInitiated, startDataPolling, stopDataPolling]);

  return shouldPoll && inPolling ? (
    <MonitorAccessPoll
      query={query}
      variables={variables}
      isVerified={isVerified}
      onVerify={onPaymentVerification}
      onPollLimitReached={onPollLimitReached}
    />
  ) : null;
};
